import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Ticket, Heart, Award, Wallet, ShoppingBag, Calendar,
  QrCode, Download, ChevronRight, Clock, DollarSign, ExternalLink, Printer
} from "lucide-react";
import { format } from "date-fns";

const downloadTicketPNG = (t) => {
  const canvas = document.createElement("canvas");
  canvas.width = 800; canvas.height = 1200;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0a1e3d"; ctx.fillRect(0,0,canvas.width,canvas.height);
  ctx.fillStyle = "#1a3a5c"; ctx.fillRect(0,0,canvas.width,140);
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 38px Arial"; ctx.textAlign = "center";
  ctx.fillText("BLUE vs. BLUE", 400, 60);
  ctx.fillStyle = "#7eb8da"; ctx.font = "20px Arial"; ctx.fillText("Event Ticket", 400, 95);
  ctx.fillStyle = "#ffffff"; ctx.font = "bold 36px Arial";
  const title = t.event_title || "Event";
  ctx.fillText(title.length > 30 ? title.slice(0,27) + "..." : title, 400, 210);
  ctx.fillStyle = "#7eb8da"; ctx.font = "20px Arial";
  ctx.fillText(t.ticket_type_name || "General Admission", 400, 250);

  const finalize = () => {
    ctx.fillStyle = "#ffffff"; ctx.font = "16px Arial";
    ctx.fillText(`Attendee: ${t.attendee_name || ""}`, 400, 880);
    ctx.fillStyle = "#7eb8da"; ctx.font = "14px monospace";
    ctx.fillText(t.qr_data || t.ticket_id, 400, 910);
    ctx.fillStyle = "#7eb8da"; ctx.font = "16px Arial";
    ctx.fillText("Present this QR at the entrance for check-in", 400, 1110);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `BvB-Ticket-${t.ticket_id}.png`;
    a.click();
    toast.success("Ticket downloaded!");
  };

  if (t.qr_code) {
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff"; ctx.fillRect(245, 350, 310, 500);
      ctx.drawImage(img, 270, 375, 260, 260);
      ctx.fillStyle = "#0a1e3d"; ctx.font = "bold 16px Arial";
      ctx.fillText("SCAN AT ENTRY", 400, 680);
      ctx.font = "14px Arial";
      ctx.fillText(`Ticket ID: ${t.ticket_id}`, 400, 710);
      ctx.fillText(`Status: ${(t.status || "valid").toUpperCase()}`, 400, 735);
      finalize();
    };
    img.onerror = () => finalize();
    img.src = `data:image/png;base64,${t.qr_code}`;
  } else { finalize(); }
};

const printTicket = (t) => {
  const w = window.open("", "_blank", "width=600,height=800");
  if (!w) { toast.error("Pop-up blocked"); return; }
  w.document.write(`<html><head><title>BvB Ticket</title><style>body{margin:0;font-family:Arial,sans-serif;padding:24px}.c{border:2px solid #0a1e3d;border-radius:12px;padding:24px;max-width:480px;margin:0 auto}h1{margin:0;font-size:24px;text-align:center}.qr{display:flex;justify-content:center;margin:16px 0}.qr img{width:240px}@media print{body{padding:0}}</style></head><body><div class="c"><h1>BLUE vs. BLUE</h1><div style="text-align:center;font-size:13px;margin:6px 0">Event Ticket</div><h2>${t.event_title || "Event"}</h2><div style="font-size:13px;line-height:1.6"><div><b>Type:</b> ${t.ticket_type_name || "General"}</div><div><b>Attendee:</b> ${t.attendee_name || ""}</div></div><div class="qr"><img src="data:image/png;base64,${t.qr_code}"/></div><div style="text-align:center;font-family:monospace;font-size:11px;background:#f1f5f9;padding:4px 8px;border-radius:4px">${t.qr_data || t.ticket_id}</div><div style="text-align:center;margin-top:12px;font-size:11px;color:#64748b">Present this QR code at entry.</div></div><script>setTimeout(()=>window.print(),300)</script></body></html>`);
  w.document.close();
};

export default function UserDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [donations, setDonations] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [wallet, setWallet] = useState({ tickets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "tickets") {
        const res = await axios.get(`${API}/user/tickets`, { withCredentials: true });
        setTickets(res.data || []);
      } else if (activeTab === "orders") {
        const res = await axios.get(`${API}/user/orders`, { withCredentials: true });
        setOrders(res.data || []);
      } else if (activeTab === "donations") {
        const res = await axios.get(`${API}/user/donations`, { withCredentials: true });
        setDonations(res.data || []);
      } else if (activeTab === "sponsorships") {
        const res = await axios.get(`${API}/user/sponsorships`, { withCredentials: true });
        setSponsorships(res.data || []);
      } else if (activeTab === "wallet") {
        const res = await axios.get(`${API}/user/wallet`, { withCredentials: true });
        setWallet(res.data || { tickets: [] });
      }
    } catch {}
    finally { setLoading(false); }
  };

  const tabs = [
    { id: "tickets", label: "My Tickets", icon: Ticket },
    { id: "wallet", label: "Wallet", icon: Wallet },
    { id: "orders", label: "Orders", icon: ShoppingBag },
    { id: "donations", label: "Donations", icon: Heart },
    { id: "sponsorships", label: "Sponsorships", icon: Award },
  ];

  return (
    <Layout>
      <section className="gradient-hero py-12">
        <div className="container-custom">
          <h1 className="text-heading-1 text-white mb-2">My Dashboard</h1>
          <p className="text-white/80">Manage your tickets, donations, and sponsorships</p>
        </div>
      </section>

      <section className="section-spacing">
        <div className="container-custom">
          {/* Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="dashboard-tabs">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === t.id ? 'bg-deep-blue text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                data-testid={`tab-${t.id}`}>
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div>
          ) : (
            <>
              {/* My Tickets */}
              {activeTab === "tickets" && (
                <div className="space-y-4" data-testid="tickets-list">
                  {tickets.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                      <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-600">No tickets yet</h3>
                      <p className="text-slate-400 mt-1 mb-4">Browse events and get your tickets!</p>
                      <Link to="/events"><Button className="bg-deep-blue text-white">Browse Events</Button></Link>
                    </div>
                  ) : tickets.map(t => (
                    <div key={t.ticket_id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 flex-wrap" data-testid={`ticket-${t.ticket_id}`}>
                      <div className="w-16 h-16 bg-deep-blue/10 rounded-lg flex items-center justify-center shrink-0">
                        {t.qr_code ? <img src={`data:image/png;base64,${t.qr_code}`} alt="QR" className="w-14 h-14" /> : <QrCode className="w-8 h-8 text-deep-blue" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-deep-blue">{t.event_title || "Event Ticket"}</h3>
                        <p className="text-sm text-slate-500">{t.ticket_type_name || t.ticket_type_id}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className={`px-2 py-0.5 rounded font-semibold ${t.status === 'valid' ? 'bg-green-100 text-green-700' : t.status === 'used' ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-700'}`}>{t.status?.toUpperCase()}</span>
                          {t.payment_status === "pending" && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">PAY AT EVENT</span>}
                        </div>
                      </div>
                      <Link to={`/my-wallet?ticket=${t.ticket_id}`}><Button variant="outline" size="sm"><Wallet className="w-4 h-4 mr-1" /> View</Button></Link>
                      {t.qr_code && t.status === 'valid' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => downloadTicketPNG(t)} data-testid={`dash-download-${t.ticket_id}`}>
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => printTicket(t)} data-testid={`dash-print-${t.ticket_id}`}>
                            <Printer className="w-4 h-4 mr-1" /> Print
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Wallet */}
              {activeTab === "wallet" && (
                <div className="space-y-4" data-testid="wallet-list">
                  {wallet.tickets?.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                      <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-600">Wallet is empty</h3>
                      <p className="text-slate-400 mt-1">Purchase event tickets to add them to your wallet</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {wallet.tickets?.map(t => (
                        <div key={t.ticket_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`wallet-ticket-${t.ticket_id}`}>
                          <div className="bg-gradient-to-r from-deep-blue to-[#1a3a5c] p-4 text-white">
                            <h3 className="font-bold">{t.event_title || "Event Ticket"}</h3>
                            <p className="text-white/70 text-sm mt-1">{user?.name}</p>
                          </div>
                          <div className="p-4 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="bg-white p-2 inline-block rounded-lg border">
                                {t.qr_code && <img src={`data:image/png;base64,${t.qr_code}`} alt="QR" className="w-32 h-32" />}
                              </div>
                              <p className="text-xs text-slate-400 mt-2 font-mono">{t.qr_data}</p>
                            </div>
                            <div className="flex flex-col gap-2">
                              <span className={`text-xs px-2 py-1 rounded font-semibold text-center ${t.status === 'valid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{t.status?.toUpperCase()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Orders */}
              {activeTab === "orders" && (
                <div className="space-y-4" data-testid="orders-list">
                  {orders.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                      <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-600">No orders yet</h3>
                    </div>
                  ) : orders.map(o => (
                    <div key={o.order_id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`order-${o.order_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-mono text-xs text-slate-400">{o.order_id}</span>
                          <h3 className="font-semibold text-deep-blue">${o.total?.toFixed(2)}</h3>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded font-semibold ${o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{o.payment_status?.toUpperCase()}</span>
                          <p className="text-xs text-slate-400 mt-1">{o.payment_method === "cash" ? "Pay at Event" : "Online"}</p>
                        </div>
                      </div>
                      <div className="text-sm text-slate-500">
                        {o.items?.map((item, i) => <div key={i}>{item.quantity}x {item.description || item.event_title || item.sponsorship_level} — ${(item.price * item.quantity).toFixed(2)}</div>)}
                      </div>
                      <p className="text-xs text-slate-400 mt-2">{o.created_at ? format(new Date(o.created_at), "MMM d, yyyy h:mm a") : ""}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Donations */}
              {activeTab === "donations" && (
                <div className="space-y-4" data-testid="donations-list">
                  {donations.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                      <Heart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-600">No donations yet</h3>
                      <p className="text-slate-400 mt-1 mb-4">Your generosity makes a difference!</p>
                      <Link to="/donate"><Button className="bg-deep-blue text-white">Make a Donation</Button></Link>
                    </div>
                  ) : donations.map(d => (
                    <div key={d.donation_id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between" data-testid={`donation-${d.donation_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center"><Heart className="w-5 h-5 text-red-500" /></div>
                        <div>
                          <h3 className="font-semibold text-deep-blue">${d.amount?.toFixed(2)}</h3>
                          <p className="text-xs text-slate-400">{d.created_at ? format(new Date(d.created_at), "MMM d, yyyy") : ""}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${d.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{d.payment_status?.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sponsorships */}
              {activeTab === "sponsorships" && (
                <div className="space-y-4" data-testid="sponsorships-list">
                  {sponsorships.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                      <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-600">No sponsorships yet</h3>
                      <p className="text-slate-400 mt-1 mb-4">Support our mission as a sponsor!</p>
                      <Link to="/get-involved"><Button className="bg-deep-blue text-white">View Sponsorship Levels</Button></Link>
                    </div>
                  ) : sponsorships.map(s => (
                    <div key={s.sponsorship_id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between" data-testid={`sponsorship-${s.sponsorship_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center"><Award className="w-5 h-5 text-amber-600" /></div>
                        <div>
                          <h3 className="font-semibold text-deep-blue">{s.level}</h3>
                          <p className="text-sm text-slate-500">${s.amount?.toFixed(2)}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-semibold ${s.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{s.payment_status?.toUpperCase()}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
